import { adminApi, adminApiInZone } from './api-client';
import { ZONES, fanOutZones, failedZoneLabels, throwIfAllZonesFailed, type PortalZone } from './zones';

/**
 * Route one per-record call to the brief's own zone (ADR-122 Patterns 1 and 2).
 *
 * ⚠ OMITTING THE ZONE MEANS "HOME", NOT "ANY". A partner application exists in exactly one
 * zone; approving or rejecting a us-east-1 brief against ap-southeast-2 returns a 404 on a
 * record the operator is looking at. The queue tags every row with `zone` so the row's actions
 * and its detail page can pass it back.
 */
const call = <T>(zone: PortalZone | undefined, path: string, init?: RequestInit): Promise<T> =>
  zone ? adminApiInZone<T>(zone, path, init) : adminApi<T>(path, init);

/**
 * Typed wrappers around the existing admin-api Partner Brief routes
 * (lambdas/admin-api/src/handlers.ts L1097–end). The Lambda is the authority
 * for the response shape; this module mirrors the `publicBrief()` projection
 * the handler returns, with the staff-portal-only fields the 5d-1 UI needs.
 *
 * Path naming note — the admin-api still uses `/admin/partner-applications/...`
 * (the 3c contract) although ADR-029 v1.2 refers to `/admin/pow/...`. The 5c
 * build picked partner-applications so the Lambda routes stay; a future ADR
 * housekeeping pass can rename if/when needed. The UI does not care which
 * path style is used.
 */

export type PartnerBriefStatus =
  | 'pending_validation'
  | 'pending_reassessment'
  | 'staff_review'
  | 'pending_response'
  | 'human_review_required'
  | 'info_requested'           // ADR-029 §2 — Request Info workflow
  | 'rejected_auto'
  | 'rejected'
  | 'rejected_poor_fit'
  | 'approved'
  | 'pending_backlink_verification'
  | 'manual_add_invited'       // ADR-032 — Manual Add Partner invitation sent (display: "Onboarding user")
  | 'manual_add_expired'       // ADR-032 — invitation TTL exceeded, resurrectable via Resend
  | 'active'
  | 'suspended'
  | 'deleted'
  | 'agent_failed';

/** Brief submission source per ADR-032. Drives display + handler branching. */
export type BriefSource = 'subscriber' | 'manual_add' | 'uat';

/**
 * Stage 1 classification per ADR-031 §1. The agent embeds this in
 * `score-breakdown.json`; for queue rendering we expose it as a sibling field
 * once the read endpoint is extended. At Phase B render-only — queue rows
 * derive classification from the score band as a fallback when the Brief
 * pre-dates ADR-031 (5c.5 D4 will land the real field).
 */
export type Stage1Classification = 'strong_fit' | 'possible_fit' | 'poor_fit';

/**
 * Queue list item — what `GET /admin/partner-applications` returns per Brief.
 * Mirrors publicBrief(brief, false) in handlers.ts.
 */
export interface BriefListItem {
  applicationId: string;
  status: PartnerBriefStatus;
  submittedAt: string;
  applicantEmail: string;
  customerNumber: string;
  businessName: string;
  websiteUrl: string;
  jurisdiction: string;
  score: number | null;
  assessmentCount: number;
  lastAssessedAt: string | null;
  scoringNote: string | null;
  humanReviewReason: string | null;
  /** Short agent verdict (≤ 15 words) — surfaced by the completion hero.
   *  Coexists with scoringNote (long form). Null on Briefs that predate
   *  the 30 May 2026 agent prompt change. */
  stage1VerdictHeadline?: string | null;
  backlinkCheckAt: string | null;
  latestS3Prefix: string | null;
  // Stage 1 / Strong Fit fields — populated when the Brief came from a
  // post-ADR-031 agent run. Old Briefs may have them null.
  stage1Classification?: Stage1Classification | null;
  strongFitBonusApplied?: boolean;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewNotes?: string;
  /** Set on optimistic queue updates after Reassess so a click-through
   *  to the Brief detail page uses this freshly-now timestamp for the
   *  progress bar instead of the stale submittedAt. */
  reassessRequestedAt?: string;
  /** ADR-032 — Brief submission source. Drives display labels (Manual
   *  Add Briefs use "Onboarding user" / "Onboarded" journey labels
   *  instead of the technical statuses). Defaults to 'subscriber' on
   *  legacy Briefs that pre-date the field. */
  briefSource?: BriefSource;
  manualAddInvitationIssuedAt?: string;
  manualAddResendCount?: number;

  /**
   * ADR-122 Pattern 1 — which zone this brief actually lives in.
   *
   * Added by the client after the fan-out, NOT returned by the server: each zone's API answers
   * only for itself and has no reason to name itself. Always populated for a row that came
   * through `listAllBriefBuckets`.
   */
  zone?: string;
}

export interface BriefDetail extends BriefListItem {
  registrationNumber: string;
  descriptionOfPractice: string;
  subscriberSub: string;
  subscriberTier: string;
  preFillValidated: boolean;
  /** Populated when status='agent_failed' — surfaced by the portal so
   *  the operator sees what went wrong instead of an empty page. */
  agentErrorMessage?: string;
  agentErrorAt?: string;
  /** Set when the operator clicks Reassess; used as the start
   *  timestamp for the in-progress hero panel so the bar starts at
   *  0%/red rather than continuing the original submittedAt clock. */
  reassessRequestedAt?: string;
}

export interface BriefListPage {
  items: BriefListItem[];
  cursor: string | null;
}

/**
 * Fetch one page of Briefs for a given status. The admin-api caps the page
 * size at 25 internally (PARTNER_APPLICATION_PAGE_LIMIT).
 */
export function listBriefsByStatus(
  status: PartnerBriefStatus,
  cursor?: string,
  /** The zone to page through. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<BriefListPage> {
  const qs = new URLSearchParams({ status });
  if (cursor) qs.set('cursor', cursor);
  return call<BriefListPage>(zone, `/admin/partner-applications?${qs.toString()}`);
}

/**
 * Fetch multiple status buckets in a SINGLE admin-api call. Replaces
 * the prior pattern of firing N parallel listBriefsByStatus calls
 * (which triggered concurrent-invocation 503s on admin-api under load).
 *
 * Returns a record keyed by status. If any individual bucket failed
 * server-side, that bucket's entry includes an `error` string and
 * empty items — the client renders partial data without surfacing the
 * error.
 */
export interface BriefBuckets {
  [status: string]: { items: BriefListItem[]; cursor: string | null; error?: string };
}

export function listBriefBuckets(
  statuses: PartnerBriefStatus[],
  /** The zone to read. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ buckets: BriefBuckets }> {
  const qs = new URLSearchParams({ statuses: statuses.join(',') });
  return call<{ buckets: BriefBuckets }>(zone, `/admin/partner-applications/buckets?${qs.toString()}`);
}

/**
 * A cursor per zone, for the "Load more" path.
 *
 * ⭐ A CURSOR IS A ZONE-LOCAL TOKEN AND MUST NEVER BE SENT TO ANOTHER ZONE. Collapsing two
 * zones' cursors into one string would page one jurisdiction using the other's bookmark — at
 * best a 400, at worst a silently wrong page of results. A zone whose cursor is `null` is
 * exhausted and is simply not asked again.
 */
export type ZoneCursors = Record<string, string | null>;

export interface BriefBucketsResult {
  buckets: BriefBuckets;
  /** Per-zone cursors for the `staff_review` bucket, keyed by zone code. */
  cursors: ZoneCursors;
  /** Labels of zones that did not answer. Empty on a complete read. */
  failedZones: string[];
}

/**
 * Every status bucket, across every zone, merged (ADR-122 Pattern 1).
 *
 * The fan-out runs in THIS BROWSER, one request per zone, each carrying the same globally-valid
 * staff token — see `lib/zones.ts` for why that is a compliance requirement and not a style
 * choice. With one zone configured this is exactly the single-zone read it replaced.
 *
 * ⚠ A ZONE'S PER-BUCKET `error` IS PRESERVED, not merged away. The server already reports a
 * bucket that failed inside an otherwise-successful response; that is a different failure from
 * a whole zone not answering, and flattening the two would hide which one happened.
 */
export async function listAllBriefBuckets(
  statuses: PartnerBriefStatus[],
): Promise<BriefBucketsResult> {
  const results = await fanOutZones(async (zone: PortalZone) => {
    const { buckets } = await listBriefBuckets(statuses, zone);
    return [{ zone, buckets }];
  });
  // No zone answered → this is a failed read, not a partial one. Let it throw so the queue
  // shows its error state with a Retry rather than an authoritative-looking empty list.
  throwIfAllZonesFailed(results);

  const merged: BriefBuckets = {};
  const cursors: ZoneCursors = {};

  for (const { zone, buckets } of results.flatMap((r) => r.items)) {
    for (const status of statuses) {
      const b = buckets[status];
      if (!b) continue;
      const into = (merged[status] ??= { items: [], cursor: null });
      into.items.push(...b.items.map((it) => ({ ...it, zone: zone.code })));
      if (b.error) into.error = b.error;
    }
    cursors[zone.code] = buckets['staff_review']?.cursor ?? null;
  }

  return { buckets: merged, cursors, failedZones: failedZoneLabels(results) };
}

/**
 * Page the `staff_review` bucket forward in every zone that still has a cursor.
 *
 * Returns the new rows (zone-tagged) plus the advanced cursor map. A zone that has run out is
 * left at `null` and skipped on the next call, so "Load more" stays available for as long as
 * ANY zone has more — not only while the home zone does.
 */
export async function loadMoreStaffReview(
  cursors: ZoneCursors,
): Promise<{ items: BriefListItem[]; cursors: ZoneCursors; failedZones: string[] }> {
  const pending = ZONES.filter((z) => cursors[z.code]);
  const settled = await Promise.allSettled(
    pending.map(async (zone) => ({
      zone,
      page: await listBriefsByStatus('staff_review', cursors[zone.code]!, zone),
    })),
  );

  const next: ZoneCursors = { ...cursors };
  const items: BriefListItem[] = [];
  const failed: string[] = [];

  settled.forEach((r, i) => {
    const zone = pending[i]!;
    if (r.status !== 'fulfilled') {
      failed.push(zone.label);
      // The cursor is LEFT AS IT WAS, not cleared: a failed page is a retryable state, and
      // clearing it would silently retire "Load more" for that zone after one bad response.
      return;
    }
    items.push(...r.value.page.items.map((it) => ({ ...it, zone: zone.code })));
    next[zone.code] = r.value.page.cursor;
  });

  return { items, cursors: next, failedZones: failed };
}

export function getBrief(
  applicationId: string,
  /** The brief's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<BriefDetail> {
  return call<BriefDetail>(zone, `/admin/partner-applications/${encodeURIComponent(applicationId)}`);
}

// =============================================================================
// Staff action dispatch (Phase 5d-1 — Decision 5-28)
// =============================================================================

export interface ApproveResponse {
  applicationId: string;
  status: 'approved';
  reviewedAt: string;
  reviewedBy: string;
  backlinkCheckAt: string;
  reviewNotes?: string;
}

export interface RejectResponse {
  applicationId: string;
  status: 'rejected';
  reviewedAt: string;
  reviewedBy: string;
  reviewNotes?: string;
}

export interface ReviewEmailResponse {
  applicationId: string;
  status: 'pending_response';
  correspondence: {
    type: 'review_email';
    performedBy: string;
    at: string;
    subject: string;
    body: string;
    returnByDate: string | null;
  };
}

export interface ActivateResponse {
  applicationId: string;
  status: 'active';
  activatedAt: string;
  activatedBy: string;
}

export function approvePartnerBrief(
  applicationId: string,
  reviewNotes?: string,
  /** The brief's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<ApproveResponse> {
  return call<ApproveResponse>(zone,
    `/admin/partner-applications/${encodeURIComponent(applicationId)}/approve`,
    {
      method: 'POST',
      body: JSON.stringify(reviewNotes ? { reviewNotes } : {}),
    },
  );
}

export function rejectPartnerBrief(
  applicationId: string,
  reviewNotes: string,
  /** The brief's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<RejectResponse> {
  return call<RejectResponse>(zone,
    `/admin/partner-applications/${encodeURIComponent(applicationId)}/reject`,
    {
      method: 'POST',
      body: JSON.stringify({ reviewNotes }),
    },
  );
}

export interface ReviewEmailRequest {
  subject: string;
  body: string;
  returnByDate?: string;
}

export function sendReviewEmail(
  applicationId: string,
  request: ReviewEmailRequest,
  /** The brief's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<ReviewEmailResponse> {
  return call<ReviewEmailResponse>(zone,
    `/admin/partner-applications/${encodeURIComponent(applicationId)}/review-email`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
  );
}

export function activatePartnerBrief(
  applicationId: string,
  /** The brief's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<ActivateResponse> {
  return call<ActivateResponse>(zone,
    `/admin/partner-applications/${encodeURIComponent(applicationId)}/activate`,
    {
      method: 'POST',
      body: JSON.stringify({}),
    },
  );
}

// =============================================================================
// Active Partner lifecycle (Phase 5d-1 follow-up, Decision 5-27 re-opened)
// =============================================================================

export interface DeactivateResponse {
  applicationId: string;
  status: 'suspended';
  suspendedAt: string;
  suspendedBy: string;
  suspensionReason: string;
}

export interface ReactivateResponse {
  applicationId: string;
  status: 'active';
  reactivatedAt: string;
  reactivatedBy: string;
  reactivationNotes?: string;
}

export function deactivatePartnerBrief(
  applicationId: string,
  reason: string,
  /** The brief's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<DeactivateResponse> {
  return call<DeactivateResponse>(zone,
    `/admin/partner-applications/${encodeURIComponent(applicationId)}/deactivate`,
    {
      method: 'POST',
      body: JSON.stringify({ reason }),
    },
  );
}

export function reactivatePartnerBrief(
  applicationId: string,
  reviewNotes?: string,
  /** The brief's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<ReactivateResponse> {
  return call<ReactivateResponse>(zone,
    `/admin/partner-applications/${encodeURIComponent(applicationId)}/reactivate`,
    {
      method: 'POST',
      body: JSON.stringify(reviewNotes ? { reviewNotes } : {}),
    },
  );
}

// =============================================================================
// UAT URL assess (Phase 5d-1 follow-up — operator diagnostic)
// =============================================================================

export interface UatAssessResponse {
  applicationId: string;
  status: 'pending_validation';
  warning?: string;
}

/** Thrown by uatAssessUrl when a Brief for the same normalised URL
 *  already exists. The caller can offer the operator a navigate-to-
 *  existing affordance. */
export class BriefAlreadyExistsError extends Error {
  applicationId: string;
  existingStatus: string;
  constructor(applicationId: string, existingStatus: string, message: string) {
    super(message);
    this.name = 'BriefAlreadyExistsError';
    this.applicationId = applicationId;
    this.existingStatus = existingStatus;
  }
}

/**
 * ⭐ THE ONE CALL IN THIS MODULE THAT CREATES A BRIEF RATHER THAN ACTING ON ONE.
 *
 * Every other function here routes to the zone a brief ALREADY lives in. This one decides that
 * zone — server-side the Brief is written by whichever zone's Lambda answers, so choosing the
 * Lambda IS the mechanism, exactly as it is for `createSubscriber` (ADR-122 Track 7).
 *
 * ⚠ OMITTING THE ZONE STILL MEANS HOME, and that remains the honest default: it is where the
 * operator's console is, and it is how a partner application arrives through the public path.
 * What changed is that the choice can now be MADE rather than only defaulted to — which is the
 * partner-side twin of Track 7, previously surfaced as ADR-122 finding #4 and decided
 * 22 September 2026.
 *
 * ⚠ THE CALLER MUST CARRY THE ZONE INTO THE BRIEF'S URL. A Brief created in us-east-1 and then
 * navigated to as `/admin/partners/{id}` with no `?zone=` would be looked up in the home zone
 * and 404 — a record that exists, reported as missing, seconds after being created. See
 * `UatAssessField`.
 */
export async function uatAssessUrl(
  url: string,
  businessName?: string,
  applicantEmail?: string,
  /** The zone to create the Brief in. Omitted = the home zone. */
  zone?: PortalZone,
  /**
   * ADR-124 D11 — the country the partner business is registered in (ISO alpha-2). REQUIRED:
   * the server refuses a Brief without one, and it is what the Brief — and later the partner's
   * own account — records. It used to be asked on the form and then dropped here, so every
   * Brief was written as Australian.
   */
  jurisdiction?: string,
): Promise<UatAssessResponse> {
  // 30 May 2026 — applicantEmail added for the Manual Add Partner panel
  // (ADR-032 §10). Backend will branch on briefSource once the
  // manualAddPartner handler lands; for now the email rides on the
  // existing /admin/diagnostics/uat-assess endpoint and is persisted to
  // the Brief's applicantEmail field.
  const body: Record<string, string> = { url };
  if (businessName) body.businessName = businessName;
  if (applicantEmail) body.applicantEmail = applicantEmail;
  if (jurisdiction) body.jurisdiction = jurisdiction;
  try {
    return await call<UatAssessResponse>(zone, '/admin/diagnostics/uat-assess', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  } catch (err) {
    // adminApi throws on non-2xx. Sniff for the 409 body shape we emit
    // server-side so the caller can branch on the dedup case.
    if (err instanceof Error && /409/.test(err.message)) {
      const m = err.message.match(/\{[\s\S]*\}/);
      if (m) {
        try {
          const body = JSON.parse(m[0]) as {
            error?: string;
            applicationId?: string;
            status?: string;
            message?: string;
          };
          if (body.error === 'brief_already_exists' && body.applicationId) {
            throw new BriefAlreadyExistsError(
              body.applicationId,
              body.status ?? 'unknown',
              body.message ?? 'A Brief for this URL already exists.',
            );
          }
        } catch (parseErr) {
          if (parseErr instanceof BriefAlreadyExistsError) throw parseErr;
        }
      }
    }
    throw err;
  }
}

export function softDeleteBrief(
  applicationId: string,
  /** The brief's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{
  applicationId: string;
  status: 'deleted';
  deletedAt: string;
  deletedBy: string;
}> {
  return call(zone, `/admin/partner-applications/${encodeURIComponent(applicationId)}`, {
    method: 'DELETE',
  });
}

/**
 * Hard-delete a Brief — irreversibly removes the DDB row and the entire
 * S3 artefact triad. Body must echo the applicationId as a confirm
 * token per the server-side gate. super_admin + fresh-auth.
 */
export function hardDeleteBrief(
  applicationId: string,
  /** The brief's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{
  applicationId: string;
  hardDeleted: true;
}> {
  return call(zone, `/admin/partner-applications/${encodeURIComponent(applicationId)}/purge`, {
    method: 'DELETE',
    body: JSON.stringify({ confirm: applicationId }),
  });
}

export function reassessBrief(
  applicationId: string,
  /** The brief's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{
  applicationId: string;
  status: 'pending_reassessment';
  warning?: string;
}> {
  return call(zone, `/admin/partner-applications/${encodeURIComponent(applicationId)}/reassess`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function restoreBrief(
  applicationId: string,
  /** The brief's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{
  applicationId: string;
  status: PartnerBriefStatus;
  restoredAt: string;
  restoredBy: string;
}> {
  return call(zone, `/admin/partner-applications/${encodeURIComponent(applicationId)}/restore`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * Fetch the four post-decision statuses for the Active Partners tab in
 * parallel and merge. At MVP volume the per-status page size of 25 covers
 * each bucket comfortably; "Load more" pagination is deferred to when one
 * bucket actually exceeds a page.
 */
export async function listActivePartnersByAllStatuses(
  /** The zone to read. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<BriefListItem[]> {
  const [approved, pendingBacklink, active, suspended] = await Promise.all([
    listBriefsByStatus('approved', undefined, zone),
    listBriefsByStatus('pending_backlink_verification', undefined, zone),
    listBriefsByStatus('active', undefined, zone),
    listBriefsByStatus('suspended', undefined, zone),
  ]);
  // Sort: active first, then pending_backlink_verification, then approved,
  // then suspended. Within each bucket, newest first by submittedAt.
  const order: Record<string, number> = {
    active: 0,
    pending_backlink_verification: 1,
    approved: 2,
    suspended: 3,
  };
  return [...approved.items, ...pendingBacklink.items, ...active.items, ...suspended.items].sort(
    (a, b) => {
      const oa = order[a.status] ?? 99;
      const ob = order[b.status] ?? 99;
      if (oa !== ob) return oa - ob;
      return b.submittedAt.localeCompare(a.submittedAt);
    },
  );
}

/**
 * Sort key used by the Applications-tab queue. Since human_review_required
 * was collapsed into staff_review (30 May 2026), the queue is effectively
 * one bucket: any legacy HRR rows tie with staff_review and are sorted by
 * submittedAt alongside them. pending_response remains lower priority.
 */
export function comparePriority(a: BriefListItem, b: BriefListItem): number {
  const order: Record<string, number> = {
    human_review_required: 0,
    staff_review: 0,
    pending_response: 1,
  };
  const aP = order[a.status] ?? 99;
  const bP = order[b.status] ?? 99;
  if (aP !== bP) return aP - bP;
  // Newest first within a priority bucket
  return b.submittedAt.localeCompare(a.submittedAt);
}

/**
 * Derive a Stage 1 classification from the score when the Brief record
 * predates ADR-031. Strong Fit ≥75; Possible Fit 30–74; Poor Fit <30. This
 * is a UI-only fallback — the actual classification on a fresh run lives in
 * `score-breakdown.json` and will populate the explicit field once 5c.5 D4
 * lands the agent update.
 */
export function classificationOrFallback(b: BriefListItem): Stage1Classification | null {
  if (b.stage1Classification) return b.stage1Classification;
  if (b.score == null) return null;
  if (b.score >= 75) return 'strong_fit';
  if (b.score >= 30) return 'possible_fit';
  return 'poor_fit';
}

/**
 * Humanised label for a PartnerBriefStatus — no underscores, sentence
 * case, suitable for status pills + section headings per admin-portal-
 * ui-standards.md §2 (no underscores rule). Falls back to a default
 * humanisation of the raw value for unknown / new statuses so the UI
 * doesn't crash when the schema evolves ahead of this map.
 */
export function partnerBriefStatusLabel(status: string, briefSource?: string, reviewedBy?: string | null): string {
  // AI vs admin distinction — the agent auto-approves/auto-activates
  // without setting reviewedBy (pow-agent/src/index.ts:411), while
  // admin Approve sets reviewedBy = ctx.staffSub. Same DDB status,
  // different decision-authority labels.
  //
  // 30 May 2026 — backlink window removed. New Approves write
  // status='active' directly; 'approved' + 'pending_backlink_verification'
  // are legacy statuses for pre-change Briefs and read as "Active".
  if (status === 'active' || status === 'approved' || status === 'pending_backlink_verification') {
    return reviewedBy ? '👤 Active (admin)' : '🤖 Active (AI)';
  }
  // Manual Add Partner Briefs (ADR-032) use friendlier journey-stage labels
  // because the BDM-facing operator narrative is "we invited them →
  // they're onboarded", not "manual_add_invited → pending_backlink_
  // verification". The underlying DDB status stays the same; only the
  // display string varies. Subscriber-originated Briefs keep the standard
  // technical labels.
  if (briefSource === 'manual_add') {
    if (status === 'manual_add_invited') return 'Onboarding user';
    if (status === 'pending_backlink_verification') return 'Onboarded';
  }
  const map: Record<string, string> = {
    pending_validation: 'Pending validation',
    pending_reassessment: 'Pending reassessment',
    staff_review: 'Staff review',
    pending_response: 'Pending response',
    human_review_required: 'Human review required',
    info_requested: 'Info requested',
    // AI vs admin differentiation (UAT 30 May 2026). Operator-facing
    // labels now make the decision authority explicit — emoji prefix
    // is the at-a-glance signal; the (AI) / (admin) suffix is the
    // unambiguous read for screen readers + filter pills.
    rejected_auto: '🤖 Rejected (AI)',
    rejected_poor_fit: '🤖 Off-target (AI)',
    rejected: '👤 Rejected (admin)',
    // 'approved' / 'pending_backlink_verification' / 'active' handled
    // above with reviewedBy lookup — kept here as fallback only.
    approved: 'Active',
    pending_backlink_verification: 'Active',
    active: 'Active',
    manual_add_invited: 'Onboarding user',
    manual_add_expired: 'Invitation expired',
    suspended: 'Suspended',
    deleted: 'Deleted',
    agent_failed: 'Agent failed',
  };
  if (map[status]) return map[status];
  // Fallback — replace underscores + capitalise the first character so
  // an unmapped status still renders human-readably.
  const cleaned = status.replace(/_/g, ' ');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}
